// SearchBar.tsx - v2.3
import React, { useState, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

interface SearchBarProps {
  onSearch: (params: SearchParams) => void;
}

export interface SearchParams {
  searchText?: string;
  sortBy?: string;
  sortDirection?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const [searchText, setSearchText] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<string>('asc');

  // Stable triggerSearch function
  const triggerSearch = useCallback(() => {
    onSearch({
      searchText: searchText.trim() || undefined,
      sortBy,
      sortDirection,
    });
  }, [searchText, sortBy, sortDirection, onSearch]);

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    // Removed debounced search trigger to prevent auto-search on typing
  };

  const handleSortByChange = (value: string) => {
    setSortBy(value);
    // Removed immediate trigger; search now only on submit
  };

  const handleSortDirectionChange = (value: string) => {
    setSortDirection(value);
    // Removed immediate trigger; search now only on submit
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    triggerSearch(); // Trigger search on form submit (Enter or button)
  };

  const handleClear = () => {
    setSearchText('');
    setSortBy('name');
    setSortDirection('asc');
    triggerSearch(); // Trigger with cleared params
  };

  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
      <form onSubmit={handleSubmit} aria-label="Search files form">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Search files"
              variant="outlined"
              value={searchText}
              onChange={(e) => handleSearchTextChange(e.target.value)}
              placeholder="Enter filename..."
              size="small"
              InputProps={{
                'aria-label': 'Search files input',
              }}
            />
          </Grid>
          
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="sort-by-label">Sort By</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                label="Sort By"
                onChange={(e) => handleSortByChange(e.target.value as string)}
                aria-label="Sort by"
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="size">Size</MenuItem>
                <MenuItem value="date">Date</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="sort-direction-label">Direction</InputLabel>
              <Select
                labelId="sort-direction-label"
                value={sortDirection}
                label="Direction"
                onChange={(e) => handleSortDirectionChange(e.target.value as string)}
                aria-label="Sort direction"
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                type="submit" 
                variant="contained" 
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
                aria-label="Search files"
              >
                Search
              </Button>
              <Tooltip title="Clear search and filters">
                <IconButton
                  onClick={handleClear}
                  color="primary"
                  aria-label="Clear search and filters"
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default SearchBar;